import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { AppLayout } from "@/components/app-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { useState, useEffect } from "react";
import { API_BASE_URL } from "@/lib/config";
import { useLanguage } from "@/components/language-provider";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Code2, Key, Plus, Trash2, Copy, CheckCircle2, Shield, RefreshCw, Loader2, AlertTriangle, Zap } from "lucide-react";

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  keyType?: string;
  isRevoked?: boolean;
  expiresAt?: string;
  createdAt: string;
  lastUsedAt?: string;
  /** Dynamics environment a Dynamics key is bound to (first use). */
  boundEnvironmentId?: string | null;
}

const labels = {
  pt: {
    title: "API & Integrações", subtitle: "Integre o FillSign com suas aplicações", apiEnabled: "API Ativa",
    tabKeys: "Chaves API", tabEndpoints: "Endpoints", tabAuth: "Autenticação",
    dynTitle: "Chave Dynamics 365 CRM", dynDesc: "Chave do componente FillSign no Dynamics 365 (PCF). Uma chave ativa por empresa, válida em um único ambiente do Dynamics: o primeiro que testar a conexão. Para um Dynamics de teste, use uma conta e chave do FillSign UAT.",
    dynBound: "Vinculada ao Dynamics", dynUnbound: "Ainda não vinculada: a chave fica presa ao primeiro ambiente do Dynamics que testar a conexão.",
    dynCreate: "Criar Chave Dynamics", dynCreating: "Criando...", dynActive: "Ativa", dynRevoked: "Revogada", dynExpired: "Expirada",
    dynExpires: "Expira em", dynNone: "Nenhuma chave Dynamics criada. Crie uma para usar o PCF no D365.",
    dynRevokedMsg: "A chave Dynamics foi revogada. Crie uma nova para reativar a integração.",
    dynRegenerate: "Regenerar Chave", dynRegenerating: "Regenerando...", dynRevoke: "Revogar", dynKeyName: "Dynamics 365 CRM",
    dynUnbind: "Desvincular", confirmUnbindTitle: "Desvincular do Dynamics?",
    confirmUnbindDesc: "A chave continua a mesma e sai deste Dynamics: ele deixa de poder usá-la, e o próximo Dynamics que testar a conexão fica com ela. Depois, tire a chave do Dynamics antigo (variável de ambiente “FillSign API Key” e, se usada, a propriedade Chave da API do formulário). Para voltar a usá-la no Dynamics antigo, regenere a chave.",
    confirmUnbindAction: "Sim, desvincular", toastUnbound: "Chave desvinculada", toastUnboundDesc: "Coloque a chave no Dynamics certo e use 🔌 Testar conexão no formulário.", toastUnbindError: "Falha ao desvincular",
    keysTitle: "Chaves API Gerais", keysDesc: "Chaves para integrações customizadas via REST API",
    keyNamePlaceholder: "Nome da chave (ex: Integração ERP)", createKey: "Criar Chave", creating: "Criando...",
    keyCreated: "Chave API Criada", keyCopied: "Copiado!", keyCreatedDesc: "Copie sua chave agora — ela não será exibida novamente.",
    yourKeys: "Suas Chaves Gerais", noKeys: "Nenhuma chave API geral criada.",
    created: "Criada", lastUsed: "Último uso", never: "Nunca", active: "Ativa", revoked: "Revogada",
    regenerate: "Regenerar", regenerating: "Regenerando...", revoke: "Revogar",
    confirmRevokeTitle: "Revogar chave API?", confirmRevokeDesc: "Esta ação é irreversível. A chave será desativada imediatamente e qualquer integração que a utilize parará de funcionar.",
    confirmRevokeAction: "Sim, revogar", cancel: "Cancelar",
    confirmRegenTitle: "Regenerar chave?", confirmRegenDesc: "A chave atual será revogada e uma nova será criada. Você precisará atualizar a chave no sistema que a utiliza.",
    confirmRegenDynDesc: "A chave Dynamics atual será revogada e uma nova será criada. Atualize-a no Dynamics: na variável de ambiente “FillSign API Key” da solução (ou na propriedade do componente, se usada).",
    confirmRegenAction: "Sim, regenerar",
    toastCreated: "Chave API criada", toastCreatedDesc: "Copie sua chave agora — ela não será exibida novamente.",
    toastRevoked: "Chave API revogada", toastRegenerated: "Chave regenerada com sucesso", toastRegeneratedDesc: "Copie a nova chave e atualize na integração.",
    toastError: "Erro", toastCreateError: "Falha ao criar chave API", toastRevokeError: "Falha ao revogar chave", toastRegenError: "Falha ao regenerar chave",
    baseUrl: "URL Base", proFeature: "Recurso Pro", proDesc: "Acesso à API REST está disponível nos planos Pro e Enterprise.", upgrade: "Fazer Upgrade",
    revokedKeys: "Chaves Revogadas",
  },
  en: {
    title: "API & Integrations", subtitle: "Integrate FillSign with your applications", apiEnabled: "API Enabled",
    tabKeys: "API Keys", tabEndpoints: "Endpoints", tabAuth: "Authentication",
    dynTitle: "Dynamics 365 CRM Key", dynDesc: "Key of the FillSign component in Dynamics 365 (PCF). One active key per company, valid in a single Dynamics environment: the first one that tests the connection. For a test Dynamics, use a FillSign UAT account and key.",
    dynBound: "Bound to Dynamics", dynUnbound: "Not bound yet: the key is tied to the first Dynamics environment that tests the connection.",
    dynCreate: "Create Dynamics Key", dynCreating: "Creating...", dynActive: "Active", dynRevoked: "Revoked", dynExpired: "Expired",
    dynExpires: "Expires", dynNone: "No Dynamics key created. Create one to use the PCF in D365.",
    dynRevokedMsg: "The Dynamics key was revoked. Create a new one to reactivate the integration.",
    dynRegenerate: "Regenerate Key", dynRegenerating: "Regenerating...", dynRevoke: "Revoke", dynKeyName: "Dynamics 365 CRM",
    dynUnbind: "Unbind", confirmUnbindTitle: "Unbind from Dynamics?",
    confirmUnbindDesc: "The key stays the same and leaves this Dynamics: it can no longer use it, and the next Dynamics that tests the connection gets it. Then remove the key from the old Dynamics (“FillSign API Key” environment variable and, if used, the form's API key property). To use it again in the old Dynamics, regenerate the key.",
    confirmUnbindAction: "Yes, unbind", toastUnbound: "Key unbound", toastUnboundDesc: "Put the key in the right Dynamics and use 🔌 Test connection on the form.", toastUnbindError: "Failed to unbind",
    keysTitle: "General API Keys", keysDesc: "Keys for custom REST API integrations",
    keyNamePlaceholder: "Key name (e.g., ERP Integration)", createKey: "Create Key", creating: "Creating...",
    keyCreated: "API Key Created", keyCopied: "Copied!", keyCreatedDesc: "Copy your key now — it won't be shown again.",
    yourKeys: "Your General Keys", noKeys: "No general API keys created.",
    created: "Created", lastUsed: "Last used", never: "Never", active: "Active", revoked: "Revoked",
    regenerate: "Regenerate", regenerating: "Regenerating...", revoke: "Revoke",
    confirmRevokeTitle: "Revoke API Key?", confirmRevokeDesc: "This action is irreversible. The key will be deactivated immediately and any integration using it will stop working.",
    confirmRevokeAction: "Yes, revoke", cancel: "Cancel",
    confirmRegenTitle: "Regenerate key?", confirmRegenDesc: "The current key will be revoked and a new one will be created. You'll need to update the key in the system that uses it.",
    confirmRegenDynDesc: "The current Dynamics key will be revoked and a new one will be created. Update it in Dynamics: in the solution's “FillSign API Key” environment variable (or in the component property, if used).",
    confirmRegenAction: "Yes, regenerate",
    toastCreated: "API key created", toastCreatedDesc: "Copy your key now — it won't be shown again.",
    toastRevoked: "API key revoked", toastRegenerated: "Key regenerated successfully", toastRegeneratedDesc: "Copy the new key and update your integration.",
    toastError: "Error", toastCreateError: "Failed to create API key", toastRevokeError: "Failed to revoke key", toastRegenError: "Failed to regenerate key",
    baseUrl: "Base URL", proFeature: "Pro Feature", proDesc: "REST API access is available for Pro and Enterprise plans.", upgrade: "Upgrade to Pro",
    revokedKeys: "Revoked Keys",
  },
};

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ApiWebhooks() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { language } = useLanguage();
  const l = labels[language] || labels.pt;

  const [user, setUser] = useState<any>(null);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [generatedDynKey, setGeneratedDynKey] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKey | null>(null);
  const [confirmRegen, setConfirmRegen] = useState<ApiKey | null>(null);
  const [confirmRegenDyn, setConfirmRegenDyn] = useState<ApiKey | null>(null);
  const [confirmUnbind, setConfirmUnbind] = useState<ApiKey | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) { setLocation("/login"); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);

  const { data: apiKeys = [], isLoading: keysLoading } = useQuery<ApiKey[]>({
    queryKey: ["api-keys"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/api/api-keys`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      return data.data || data;
    },
    enabled: !!user && (user.isSuperAdmin || user.plan === "pro" || user.plan === "enterprise" || user.plan === "custom_enterprise"),
  });

  const dynamicsKeys = apiKeys.filter(k => k.keyType === "dynamics");
  const activeDynKey = dynamicsKeys.find(k => !k.isRevoked);
  const revokedDynKeys = dynamicsKeys.filter(k => k.isRevoked);
  const generalKeys = apiKeys.filter(k => k.keyType !== "dynamics");
  const activeGeneralKeys = generalKeys.filter(k => !k.isRevoked);
  const revokedGeneralKeys = generalKeys.filter(k => k.isRevoked);

  const createKeyMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch(`${API_BASE_URL}/api/api-keys`, {
        method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeaders() }, body: JSON.stringify({ name }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || `Error ${res.status}`); }
      const data = await res.json(); return data.data || data;
    },
    onSuccess: (data) => { setGeneratedKey(data.key); setNewKeyName(""); queryClient.invalidateQueries({ queryKey: ["api-keys"] }); toast({ title: l.toastCreated, description: l.toastCreatedDesc }); },
    onError: (error: any) => { toast({ title: l.toastCreateError, description: error.message, variant: "destructive" }); },
  });

  const createDynKeyMutation = useMutation({
    mutationFn: async () => {
      const companyId = user?.companyId;
      if (!companyId) throw new Error("No company found. Create a company first in Settings.");
      const res = await fetch(`${API_BASE_URL}/api/api-keys/dynamics`, {
        method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ name: l.dynKeyName, companyId }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || `Error ${res.status}`); }
      const data = await res.json(); return data.data || data;
    },
    onSuccess: (data) => { setGeneratedDynKey(data.key); queryClient.invalidateQueries({ queryKey: ["api-keys"] }); toast({ title: l.toastCreated, description: l.toastCreatedDesc }); },
    onError: (error: any) => { toast({ title: l.toastCreateError, description: error.message, variant: "destructive" }); },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE_URL}/api/api-keys/${id}`, { method: "DELETE", headers: getAuthHeaders() });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || `Error ${res.status}`); }
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["api-keys"] }); toast({ title: l.toastRevoked }); setConfirmRevoke(null); },
    onError: (error: any) => { toast({ title: l.toastRevokeError, description: error.message, variant: "destructive" }); },
  });

  const regenerateKeyMutation = useMutation({
    mutationFn: async (oldKey: ApiKey) => {
      await fetch(`${API_BASE_URL}/api/api-keys/${oldKey.id}`, { method: "DELETE", headers: getAuthHeaders() });
      const res = await fetch(`${API_BASE_URL}/api/api-keys`, {
        method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeaders() }, body: JSON.stringify({ name: oldKey.name }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || "Failed"); }
      const data = await res.json(); return data.data || data;
    },
    onSuccess: (data) => { setGeneratedKey(data.key); setConfirmRegen(null); queryClient.invalidateQueries({ queryKey: ["api-keys"] }); toast({ title: l.toastRegenerated, description: l.toastRegeneratedDesc }); },
    onError: (error: any) => { toast({ title: l.toastRegenError, description: error.message, variant: "destructive" }); setConfirmRegen(null); },
  });

  const regenerateDynKeyMutation = useMutation({
    mutationFn: async (oldKey: ApiKey) => {
      const companyId = user?.companyId;
      if (!companyId) throw new Error("No company found");
      await fetch(`${API_BASE_URL}/api/api-keys/${oldKey.id}`, { method: "DELETE", headers: getAuthHeaders() });
      const res = await fetch(`${API_BASE_URL}/api/api-keys/dynamics`, {
        method: "POST", headers: { "Content-Type": "application/json", ...getAuthHeaders() }, body: JSON.stringify({ name: l.dynKeyName, companyId }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || "Failed"); }
      const data = await res.json(); return data.data || data;
    },
    onSuccess: (data) => { setGeneratedDynKey(data.key); setConfirmRegenDyn(null); queryClient.invalidateQueries({ queryKey: ["api-keys"] }); toast({ title: l.toastRegenerated, description: l.toastRegeneratedDesc }); },
    onError: (error: any) => { toast({ title: l.toastRegenError, description: error.message, variant: "destructive" }); setConfirmRegenDyn(null); },
  });

  // Same key, free again: the next Dynamics environment that tests the connection binds it.
  const unbindDynKeyMutation = useMutation({
    mutationFn: async (key: ApiKey) => {
      const res = await fetch(`${API_BASE_URL}/api/api-keys/dynamics/${key.id}/unbind`, { method: "POST", headers: getAuthHeaders() });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || `Error ${res.status}`); }
    },
    onSuccess: () => { setConfirmUnbind(null); queryClient.invalidateQueries({ queryKey: ["api-keys"] }); toast({ title: l.toastUnbound, description: l.toastUnboundDesc }); },
    onError: (error: any) => { toast({ title: l.toastUnbindError, description: error.message, variant: "destructive" }); setConfirmUnbind(null); },
  });

  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); setLocation("/"); };
  const copyToClipboard = (text: string) => { navigator.clipboard.writeText(text); toast({ title: l.keyCopied }); };

  if (!user) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-pulse text-muted-foreground">{language === "pt" ? "Carregando..." : "Loading..."}</div></div>;

  const isPro = user.isSuperAdmin || user.plan === "pro" || user.plan === "enterprise" || user.plan === "custom_enterprise";
  if (!isPro) {
    return (
      <AppLayout user={user} onLogout={handleLogout} plan={user.plan} currentPage="/api-webhooks">
        <div className="space-y-6">
          <div><h1 className="text-2xl font-semibold">{l.title}</h1><p className="text-muted-foreground mt-1">{l.subtitle}</p></div>
          <Card><CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Code2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">{l.proFeature}</h3>
            <p className="text-muted-foreground mb-4 max-w-md">{l.proDesc}</p>
            <Button onClick={() => setLocation("/pricing")}>{l.upgrade}</Button>
          </CardContent></Card>
        </div>
      </AppLayout>
    );
  }

  const dynKeyExpired = activeDynKey?.expiresAt && new Date(activeDynKey.expiresAt) < new Date();

  return (
    <AppLayout user={user} onLogout={handleLogout} plan={user.plan} currentPage="/api-webhooks">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div><h1 className="text-2xl font-semibold">{l.title}</h1><p className="text-muted-foreground mt-1">{l.subtitle}</p></div>
          <Badge variant="outline" className="gap-1 shrink-0"><CheckCircle2 className="h-3 w-3 text-green-500" />{l.apiEnabled}</Badge>
        </div>

        <Tabs defaultValue="keys" className="space-y-6">
          <TabsList>
            <TabsTrigger value="keys">{l.tabKeys}</TabsTrigger>
            <TabsTrigger value="overview">{l.tabEndpoints}</TabsTrigger>
            <TabsTrigger value="authentication">{l.tabAuth}</TabsTrigger>
          </TabsList>

          <TabsContent value="keys" className="space-y-6">
            {/* ═══ DYNAMICS 365 KEY ═══ */}
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-blue-500" />{l.dynTitle}</CardTitle>
                <CardDescription>{l.dynDesc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {generatedDynKey && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-green-500" /><span className="font-medium text-green-600 dark:text-green-400">{l.keyCreated}</span></div>
                    <p className="text-sm text-muted-foreground mb-3">{l.keyCreatedDesc}</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2.5 rounded bg-muted font-mono text-sm break-all select-all">{generatedDynKey}</code>
                      <Button size="icon" variant="outline" onClick={() => copyToClipboard(generatedDynKey)}><Copy className="h-4 w-4" /></Button>
                    </div>
                  </div>
                )}
                {keysLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">{language === "pt" ? "Carregando..." : "Loading..."}</span></div>
                ) : activeDynKey ? (
                  <div className="p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{activeDynKey.name}</p>
                          {dynKeyExpired
                            ? <Badge variant="destructive" className="text-xs">{l.dynExpired}</Badge>
                            : <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800 text-xs">{l.dynActive}</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground font-mono">{activeDynKey.keyPrefix}...</p>
                        <p className="text-xs text-muted-foreground">
                          {l.created}: {new Date(activeDynKey.createdAt).toLocaleDateString()}
                          {activeDynKey.expiresAt && ` · ${l.dynExpires}: ${new Date(activeDynKey.expiresAt).toLocaleDateString()}`}
                          {activeDynKey.lastUsedAt ? ` · ${l.lastUsed}: ${new Date(activeDynKey.lastUsedAt).toLocaleDateString()}` : ` · ${l.lastUsed}: ${l.never}`}
                        </p>
                        <p className="text-xs text-muted-foreground" data-testid="dyn-bound">
                          {activeDynKey.boundEnvironmentId
                            ? <>{l.dynBound}: <span className="font-mono">{activeDynKey.boundEnvironmentId}</span>{" · "}
                                <button type="button" className="underline underline-offset-2 hover:text-foreground disabled:opacity-50" data-testid="dyn-unbind"
                                  onClick={() => setConfirmUnbind(activeDynKey)} disabled={unbindDynKeyMutation.isPending}>{l.dynUnbind}</button></>
                            : l.dynUnbound}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setConfirmRegenDyn(activeDynKey)} disabled={regenerateDynKeyMutation.isPending}>
                          {regenerateDynKeyMutation.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />{l.dynRegenerating}</> : <><RefreshCw className="h-3.5 w-3.5" />{l.dynRegenerate}</>}
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setConfirmRevoke(activeDynKey)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Zap className="h-8 w-8 mx-auto mb-3 text-muted-foreground opacity-40" />
                    <p className="text-sm text-muted-foreground mb-1">{revokedDynKeys.length > 0 ? l.dynRevokedMsg : l.dynNone}</p>
                    <Button className="mt-3 gap-2" onClick={() => createDynKeyMutation.mutate()} disabled={createDynKeyMutation.isPending}>
                      {createDynKeyMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin" />{l.dynCreating}</> : <><Zap className="h-4 w-4" />{l.dynCreate}</>}
                    </Button>
                  </div>
                )}
                {revokedDynKeys.length > 0 && (
                  <div className="pt-3 border-t space-y-2">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" />{l.revokedKeys} ({revokedDynKeys.length})</p>
                    {revokedDynKeys.map((key) => (
                      <div key={key.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-dashed opacity-60">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2"><p className="font-medium line-through">{key.name}</p><Badge variant="secondary" className="text-xs">{l.dynRevoked}</Badge></div>
                          <p className="text-sm text-muted-foreground font-mono">{key.keyPrefix}...</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ═══ GENERAL KEYS ═══ */}
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Key className="h-5 w-5" />{l.keysTitle}</CardTitle><CardDescription>{l.keysDesc}</CardDescription></CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input placeholder={l.keyNamePlaceholder} value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && newKeyName && createKeyMutation.mutate(newKeyName)} />
                  <Button onClick={() => newKeyName && createKeyMutation.mutate(newKeyName)} disabled={!newKeyName || createKeyMutation.isPending}>
                    {createKeyMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{l.creating}</> : <><Plus className="h-4 w-4 mr-2" />{l.createKey}</>}
                  </Button>
                </div>
                {generatedKey && (
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4 text-green-500" /><span className="font-medium text-green-600 dark:text-green-400">{l.keyCreated}</span></div>
                    <p className="text-sm text-muted-foreground mb-3">{l.keyCreatedDesc}</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2.5 rounded bg-muted font-mono text-sm break-all select-all">{generatedKey}</code>
                      <Button size="icon" variant="outline" onClick={() => copyToClipboard(generatedKey)}><Copy className="h-4 w-4" /></Button>
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  <h4 className="font-medium">{l.yourKeys}</h4>
                  {activeGeneralKeys.length === 0 && revokedGeneralKeys.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground"><Key className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">{l.noKeys}</p></div>
                  ) : (
                    <>
                      {activeGeneralKeys.map((key) => (
                        <div key={key.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 rounded-lg border hover:border-primary/30 transition-colors">
                          <div className="space-y-1 min-w-0">
                            <div className="flex items-center gap-2"><p className="font-medium">{key.name}</p><Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800 text-xs">{l.active}</Badge></div>
                            <p className="text-sm text-muted-foreground font-mono">{key.keyPrefix}...</p>
                            <p className="text-xs text-muted-foreground">{l.created}: {new Date(key.createdAt).toLocaleDateString()}{key.lastUsedAt ? ` · ${l.lastUsed}: ${new Date(key.lastUsedAt).toLocaleDateString()}` : ` · ${l.lastUsed}: ${l.never}`}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setConfirmRegen(key)} disabled={regenerateKeyMutation.isPending}>
                              {regenerateKeyMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}{l.regenerate}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => setConfirmRevoke(key)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                          </div>
                        </div>
                      ))}
                      {revokedGeneralKeys.length > 0 && (
                        <div className="pt-3 border-t space-y-2">
                          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide flex items-center gap-1.5"><AlertTriangle className="h-3 w-3" />{l.revokedKeys} ({revokedGeneralKeys.length})</p>
                          {revokedGeneralKeys.map((key) => (
                            <div key={key.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-dashed opacity-60">
                              <div className="space-y-1"><div className="flex items-center gap-2"><p className="font-medium line-through">{key.name}</p><Badge variant="secondary" className="text-xs">{l.revoked}</Badge></div><p className="text-sm text-muted-foreground font-mono">{key.keyPrefix}...</p></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="overview" className="space-y-6">
            <Card><CardHeader><CardTitle>{l.baseUrl}</CardTitle></CardHeader><CardContent><div className="flex items-center gap-2">
              <code className="flex-1 p-3 rounded-lg bg-muted font-mono text-sm">{API_BASE_URL}</code>
              <Button size="icon" variant="outline" onClick={() => copyToClipboard(API_BASE_URL)}><Copy className="h-4 w-4" /></Button>
            </div></CardContent></Card>
          </TabsContent>

          <TabsContent value="authentication" className="space-y-6">
            <Card><CardHeader><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" />{l.tabAuth}</CardTitle></CardHeader><CardContent>
              <div className="p-4 rounded-lg bg-muted font-mono text-sm overflow-x-auto">
                <pre>{`curl -X POST "${API_BASE_URL}/api/v1/integrations/validate-key" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "X-CRM-Source: D365"`}</pre>
              </div>
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Confirm Revoke */}
      <AlertDialog open={!!confirmRevoke} onOpenChange={(open) => !open && setConfirmRevoke(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{l.confirmRevokeTitle}</AlertDialogTitle>
          <AlertDialogDescription>{l.confirmRevokeDesc}{confirmRevoke && <span className="block mt-2 font-mono text-sm bg-muted p-2 rounded">{confirmRevoke.name} ({confirmRevoke.keyPrefix}...)</span>}</AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>{l.cancel}</AlertDialogCancel>
          <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => confirmRevoke && deleteKeyMutation.mutate(confirmRevoke.id)}>{l.confirmRevokeAction}</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Confirm Regen General */}
      <AlertDialog open={!!confirmRegen} onOpenChange={(open) => !open && setConfirmRegen(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{l.confirmRegenTitle}</AlertDialogTitle>
          <AlertDialogDescription>{l.confirmRegenDesc}{confirmRegen && <span className="block mt-2 font-mono text-sm bg-muted p-2 rounded">{confirmRegen.name} ({confirmRegen.keyPrefix}...)</span>}</AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>{l.cancel}</AlertDialogCancel>
          <AlertDialogAction onClick={() => confirmRegen && regenerateKeyMutation.mutate(confirmRegen)}><RefreshCw className="h-4 w-4 mr-2" />{l.confirmRegenAction}</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Confirm Regen Dynamics */}
      <AlertDialog open={!!confirmRegenDyn} onOpenChange={(open) => !open && setConfirmRegenDyn(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{l.confirmRegenTitle}</AlertDialogTitle>
          <AlertDialogDescription>{l.confirmRegenDynDesc}{confirmRegenDyn && <span className="block mt-2 font-mono text-sm bg-muted p-2 rounded">{confirmRegenDyn.name} ({confirmRegenDyn.keyPrefix}...)</span>}</AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>{l.cancel}</AlertDialogCancel>
          <AlertDialogAction onClick={() => confirmRegenDyn && regenerateDynKeyMutation.mutate(confirmRegenDyn)}><RefreshCw className="h-4 w-4 mr-2" />{l.confirmRegenAction}</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>

      {/* Confirm Unbind Dynamics */}
      <AlertDialog open={!!confirmUnbind} onOpenChange={(open) => !open && setConfirmUnbind(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{l.confirmUnbindTitle}</AlertDialogTitle>
          <AlertDialogDescription>{l.confirmUnbindDesc}{confirmUnbind?.boundEnvironmentId && <span className="block mt-2 font-mono text-sm bg-muted p-2 rounded">{confirmUnbind.boundEnvironmentId}</span>}</AlertDialogDescription>
        </AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>{l.cancel}</AlertDialogCancel>
          <AlertDialogAction data-testid="dyn-unbind-confirm" onClick={() => confirmUnbind && unbindDynKeyMutation.mutate(confirmUnbind)}>{l.confirmUnbindAction}</AlertDialogAction>
        </AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
